import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  CreateWorkflowResponse,
  ListWorkflowsOptions,
  ListWorkflowsResponse,
  Workflow,
  WorkflowInfo,
  createWorkflow as universalCreateWorkflow,
  deleteWorkflow as universalDeleteWorkflow,
  getWorkflow as universalGetWorkflow,
  listWorkflows as universalListWorkflows,
  updateWorkflow as universalUpdateWorkflow,
} from './workflows-v1-workflow-workflows.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/workflows' };

export function listWorkflows(httpClient: HttpClient): ListWorkflowsSignature {
  return (options?: ListWorkflowsOptions) =>
    universalListWorkflows(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListWorkflowsSignature {
  /**
   * Retrieves a list of the site's workflows info.
   *
   *
   * The `listWorkflows()` function returns a Promise that resolves to a list containing information about the site's workflows.
   *
   *
   * Use the `options` parameter to specify which workflows to retrieve and in which order to retrieve them. Workflows can be sorted based on their `"name"`.
   *
   *
   * If no `limit` parameter is passed, the first 100 workflows are returned. Sort order defaults to by `"name"` ascending.
   * @param - Options to use when retrieving a list of workflows.
   */
  (options?: ListWorkflowsOptions): Promise<
    NonNullablePaths<
      ListWorkflowsResponse,
      | `workflows`
      | `pagination.offset`
      | `pagination.total`
      | `pagination.count`,
      3
    >
  >;
}

export function getWorkflow(httpClient: HttpClient): GetWorkflowSignature {
  return (_id: string, cardsPerPhase: number) =>
    universalGetWorkflow(
      _id,
      cardsPerPhase,
      // @ts-ignore
      { httpClient }
    );
}

interface GetWorkflowSignature {
  /**
   * Retrieves a workflow by ID.
   *
   *
   * The `getWorkflow()` function returns a Promise that resolves to the workflow info with the specified ID.
   *
   *
   * This function requires you to specify the ID of a workflow. To learn about retrieving IDs in the Workflows APIs, see [Retrieving IDs](wix-workflows-v2/introduction#retrieving-ids).
   * @param - ID of the workflow to retrieve.
   * @param - Maximum number of cards to return per phase.
   * To retrieve additional pages of cards, use
   * [List Cards](../cards/listCards)
   * (in the Cards API).
   * @param - Options to use when retrieving a workflow.
   * @returns Requested workflow.
   */
  (_id: string, cardsPerPhase: number): Promise<
    NonNullablePaths<
      Workflow,
      | `winPhase.cardsList.total`
      | `winPhase.cardsList.cards`
      | `winPhase.cardsList.cards.${number}.info.primaryAttachment.attachmentId`
      | `winPhase.cardsList.cards.${number}.info.primaryAttachment.value`
      | `winPhase.cardsList.cards.${number}.info.primaryAttachment.attachmentType`
      | `winPhase.cardsList.cards.${number}.info.primaryAttachment.contactId`
      | `phasesList.total`
      | `phasesList.phases`,
      8
    >
  >;
}

export function updateWorkflow(
  httpClient: HttpClient
): UpdateWorkflowSignature {
  return (_id: string, workflow: WorkflowInfo) =>
    universalUpdateWorkflow(
      _id,
      workflow,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateWorkflowSignature {
  /**
   * Updates an existing workflow.
   *
   *
   * The `updateWorkflow()` function returns a Promise that resolves when the workflow has been updated with the specified values.
   *
   *
   * Only the properties passed in the `workflow` object will be updated. All other properties will remain the same.
   *
   *
   * This function requires you to specify the ID of a workflow. To learn about retrieving IDs in the Workflow API, see [Retrieving IDs](#retrieving-ids).
   * @param - ID of the workflow to update.
   * @param - Workflow info.
   * @param - Options to use when updating a workflow.
   */
  (_id: string, workflow: WorkflowInfo): Promise<void>;
}

export function createWorkflow(
  httpClient: HttpClient
): CreateWorkflowSignature {
  return (workflow: NonNullablePaths<WorkflowInfo, `name`, 2>) =>
    universalCreateWorkflow(
      workflow,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateWorkflowSignature {
  /**
   * Creates a new workflow.
   *
   *
   * The `createWorkflow()` function returns a Promise that resolves to the created workflow ID.
   * @param - Workflow details.
   * @param - Options to use when creating a workflow.
   */
  (
    workflow: NonNullablePaths<WorkflowInfo, `name`, 2>
  ): Promise<CreateWorkflowResponse>;
}

export function deleteWorkflow(
  httpClient: HttpClient
): DeleteWorkflowSignature {
  return (_id: string) =>
    universalDeleteWorkflow(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteWorkflowSignature {
  /**
   * Deletes a workflow.
   *
   *
   * The `deleteWorkflow()` function returns a Promise when the specified workflow has been deleted.
   *
   *
   * This function requires you to specify the ID of a workflow. To learn about retrieving IDs in the Workflows APIs, see [Retrieving IDs](wix-workflows-v2/introduction#retrieving-ids).
   * @param - ID of the workflow to delete.
   */
  (_id: string): Promise<void>;
}

export {
  Attachment,
  AttachmentType,
  Card,
  CardInfo,
  CardsList,
  CreateWorkflowRequest,
  CreateWorkflowResponse,
  DeleteWorkflowRequest,
  DeleteWorkflowResponse,
  GetWorkflowRequest,
  GetWorkflowResponse,
  ListWorkflowsOptions,
  ListWorkflowsRequest,
  ListWorkflowsResponse,
  PaginationResponse,
  Paging,
  Phase,
  PhaseInfo,
  PhasesList,
  Query,
  QueryWorkflowsRequest,
  QueryWorkflowsResponse,
  SetupWorkflowRequest,
  SetupWorkflowResponse,
  SortOrder,
  Sorting,
  UpdateWorkflowRequest,
  UpdateWorkflowResponse,
  Workflow,
  WorkflowInfo,
} from './workflows-v1-workflow-workflows.universal.js';
